import React, { useEffect, useRef } from 'react';
import { PickSelect } from './controls';
import { fmtDateLong } from '../state/format';
import { SIDE, TIMING_SIDE_CHIP } from '../../constants';

const e = React.createElement;

/**
 * One interview sitting: which day the group sits on.
 *
 * A sitting is not an event on the timeline, so it is not ordered and it
 * does not hold the crew for a slot. Neither the event nor the hour is
 * asked for: the coordinator plans the sitting into the day once the crew
 * call is drawn up. One question is all that is left, so this reads as a
 * row in a list rather than as a card with a single field inside it.
 */
const InterviewCard = ({ slot, locked, onChange }) => {
  const shut = !!locked;
  // Nothing to offer means no day this group may sit on: either no
  // cinematographer is booked on any event, or the only filmed events are
  // the ceremonies, which are the couple's own sitting.
  const offered = slot.dates || [];
  const nowhereToSit = !offered.length;
  // Said on the row rather than inside the field, because a disabled
  // control takes no pointer of its own. Only for the family sittings: a
  // sitting is filmed by a cinematographer, so with none booked on any day
  // there is nothing to offer and the row would otherwise sit shut without
  // saying why. The bride's and the groom's own sittings run at the
  // wedding whoever is shooting it, so that is not their reason.
  const ownSitting = slot.group === slot.side;
  const whyShut = nowhereToSit && !ownSitting
    ? 'No cinematographer available on any day'
    : undefined;
  const dateOptions = [{
    value: '',
    label: offered.length ? 'Select a day' : 'No day available',
  }].concat(
    offered.map((date) => ({ value: date, label: fmtDateLong(date) }))
  );

  // One day on offer is not a choice, so it is said rather than asked. It
  // is answered here too: a row the couple cannot answer any other way
  // would hold the section open on a day that was never in doubt.
  const only = offered.length === 1 ? offered[0] : null;
  const answered = useRef('');
  useEffect(() => {
    // Sent once. A server that refuses it puts the row back as it was,
    // and asking again on every render would make that refusal a loop.
    if (!only || shut || slot.date === only || answered.current === only) {
      return;
    }
    answered.current = only;
    onChange({ date: only });
  });

  return e('div', { className: 'wd-ivrow' }, [
    e('div', { className: 'wd-ivwho', key: 'who' }, [
      e('span', { className: 'wd-nm', key: 'nm' }, slot.label),
      e('span', { className: 'wd-badge', key: 'b' },
        slot.side === SIDE.BRIDE ? TIMING_SIDE_CHIP[SIDE.BRIDE] : TIMING_SIDE_CHIP[SIDE.GROOM]),
    ]),
    e('div', { className: 'wd-ivpick', key: 'pick', title: whyShut }, [
      // The day, not an event on it. Only the days this group may sit on
      // are offered, which the payload has already narrowed.
      //
      // The form's own picker, not the browser's: a native select opens as
      // a platform wheel on a phone, which hides the days the couple is
      // choosing between.
      only
      ? e('div', { className: 'wd-timelocked', key: 'day' },
          fmtDateLong(only))
      : e(PickSelect, {
        key: 'day',
        label: 'Day',
        ariaLabel: `Day for the ${slot.label} sitting`,
        value: slot.date || '',
        options: dateOptions,
        disabled: shut || nowhereToSit,
        onChange: (v) => onChange({ date: v || '' }),
      }),
    ]),
  ]);
};

export default InterviewCard;
